import axios from 'axios'
import { User } from '../Types/user'
import { UserTypes } from '../Types/userTypes'
import qs from 'qs'
import { NewPortfolioLink, PortfolioLink } from '../Types/portfolioLinks'

class UserClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  createUser = async (auth0Id: string, email: string, userType: UserTypes, accessToken: string): Promise<User> => {
    const response = await axios({
      method: 'post',
      url: this.baseUrl,
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      data: {
        auth0Id,
        email,
        userType,
      },
    })

    return response.data
  }

  updateUser = async (
    id: string,
    userDto: {
      firstName?: string
      lastName?: string
      email?: string
      about?: string
      locationId?: string
      locationFormattedString?: string
      locationGeometry?: {
        type: string // "Point"
        coordinates: [number, number] // [longitude, latitude]
      }
      youTubeUrl?: string
      spotifyUrl?: string
      soundCloudUrl?: string
      profileImageUrl?: string
      profileAudioUrl?: string
    },
    accessToken: string,
  ): Promise<User> => {
    const response = await axios({
      method: 'patch',
      url: `${this.baseUrl}/${id}`,
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      data: userDto,
    })

    return response.data
  }

  getUserById = async (id: string): Promise<User> => {
    const response = await axios({
      method: 'get',
      url: `${this.baseUrl}/${id}`,
    })
    return response.data
  }

  getUserByAuth0Id = async (auth0Id: string, accessToken: string): Promise<User | undefined> => {
    try {
      const response = await axios({
        method: 'get',
        url: this.baseUrl,
        params: { auth0Id },
        headers: { Authorization: `Bearer ${accessToken}` },
      })

      if (response.status === 200) return response.data
      return undefined
    } catch (error: any) {
      if (error.response) {
        if (error.response.status === 404) {
          return undefined
        }
      } else {
        console.error('Network or other error:', error.message)
      }
      return undefined
    }
  }

  overwriteUserSkills = async (id: string, skills: string[], accessToken: string) => {
    const response = await axios({
      method: 'post',
      url: `${this.baseUrl}/${id}/skills`,
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      data: skills,
    })

    if (response && response.status == 200) {
      return response.status
    }
  }

  overwriteUserGenres = async (id: string, genres: { id: string; name: string }[], accessToken: string) => {
    const response = await axios({
      method: 'post',
      url: `${this.baseUrl}/${id}/genres`,
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      data: genres,
    })

    if (response && response.status == 200) {
      return response.status
    }
  }

  overwriteUserOfferings = async (id: string, offerings: { id: string; name: string }[], accessToken: string) => {
    const response = await axios({
      method: 'post',
      url: `${this.baseUrl}/${id}/offerings`,
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      data: offerings,
    })

    if (response && response.status == 200) {
      return response.status
    }
  }

  updatePortfolioLinks = async (
    id: string,
    portfolioLink: NewPortfolioLink,
    accessToken: string,
  ): Promise<PortfolioLink> => {
    const response = await axios({
      method: 'post',
      url: `${this.baseUrl}/${id}/portfolioLinks`,
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      data: {
        ...portfolioLink,
        userId: id,
      },
    })

    if (response && response.status === 200) {
      return response.data
    }
    throw new Error('Failed to update portfolio links')
  }

  removePortfolioLink = async (userId: string, linkId: string, accessToken: string): Promise<PortfolioLink[]> => {
    const response = await axios({
      method: 'delete',
      url: `${this.baseUrl}/${userId}/portfolioLinks/${linkId}`,
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    })

    if (response && response.status === 200) {
      return response.data
    }
    throw new Error('Failed to remove portfolio link')
  }

  searchUsers = async (
    searchParams: {
      search?: string
      offerings?: string[]
      genres?: string[]
      longitude?: number
      latitude?: number
      page?: number
    },
    accessToken: string,
  ): Promise<User[] | undefined> => {
    try {
      const response = await axios({
        method: 'get',
        url: `${this.baseUrl}/search`,
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        params: searchParams,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })

      if (response && response.status == 200) {
        return response.data
      }
    } catch (error) {
      console.log('')
    }
  }

  sendMessageToMusician = async (
    id: string,
    body: string,
    senderEmail: string,
    accessToken: string,
  ): Promise<boolean> => {
    try {
      const response = await axios({
        method: 'post',
        url: `${this.baseUrl}/${id}/message`,
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        data: {
          senderEmail,
          body,
        },
      })

      return response.status === 200
    } catch (error) {
      console.error('Error sending message to musician:', error)
      return false
    }
  }
}

const userClient = new UserClient(`${process.env.REACT_APP_API_URL}users`)
export default userClient
